import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CommentService } from 'src/app/services/comment/comment.service';
import { CommonService } from 'src/app/services/common/common.service';
import { environment } from 'src/environments/environment';
import { CommentPreviewModalComponent } from './comment-preview-modal.component';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
})
export class CommentsComponent implements OnInit {
  @Input() objectType = null;
  @Input() objectId = null;

  comment: any = '';
  comments: any = [];
  attachments: any[] = [];
  profile: any = {};
  timelineGroups: Array<{ label: string; items: any[] }> = [];
  isAttachmentProcessing = false;

  constructor(
    private commentService: CommentService,
    private auth: AuthService,
    private commonService: CommonService,
    private modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.profile = this.auth.getLoggedUser() || {};
    this.commentService
      .list({
        objectType: this.objectType,
        objectId: this.objectId,
        $sort: { createdAt: -1 },
        $populate: ['author']
      })
      .subscribe((resp) => {
        const normalized = (resp?.data || []).map((item: any) => this.normalizeComment(item));
        this.comments = normalized.sort(
          (a: any, b: any) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
        );
        this.rebuildTimelineGroups();
      });
  }

  get canSend(): boolean {
    return !!(`${this.comment || ''}`.trim() || this.attachments.length);
  }

  async onFileSelected(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.isAttachmentProcessing = true;
    try {
      const preparedImage = this.isImageFile(file)
        ? await this.prepareImageForUpload(file)
        : { file, locationText: '' };
      const uploadFile = preparedImage.file;
      const resp: any = await this.uploadAttachmentFile(uploadFile);
      const filename = resp?.image?.filename;
      if (!filename) return;

      this.attachments.push({
        name: uploadFile.name,
        filesize: uploadFile.size / (1024 * 1024),
        filetype: uploadFile.type,
        url: `${environment.taskSharingApiUrl}/uploads/${filename}`,
        creationDate: new Date().toISOString(),
        uploadedById: this.profile?.id || null,
        uploadedByEmail: this.profile?.email || null,
        location: preparedImage.locationText || null,
      });
    } catch {
      console.error('Comment attachment upload failed');
    } finally {
      this.isAttachmentProcessing = false;
      // allow picking same file again
      event.target.value = '';
    }
  }

  getAttachmentLocation(attachment: any): string {
    return `${attachment?.location || attachment?.locationText || ''}`.trim();
  }

  removeAttachment(attachment: any) {
    this.attachments = this.attachments.filter((item) => item !== attachment);
  }

  getAuthorName(item: any): string {
    const author = item?.author || {};
    return (
      author?.fullname ||
      author?.fullName ||
      author?.name ||
      author?.username ||
      author?.email ||
      this.profile?.fullname ||
      this.profile?.fullName ||
      this.profile?.name ||
      this.profile?.email ||
      'Anonymous'
    );
  }

  getAuthorAvatar(item: any): string {
    return item?.author?.thumbnail || this.profile?.thumbnail || 'assets/images/Blank_Avatar.png';
  }

  getAttachmentUrl(attachment: any): string {
    return attachment?.url || attachment?.base64 || '';
  }

  isImageAttachment(attachment: any): boolean {
    const filetype = `${attachment?.filetype || ''}`.toLowerCase();
    if (filetype.startsWith('image/')) return true;

    const name = `${attachment?.name || ''}`.toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
  }

  isVideoAttachment(attachment: any): boolean {
    const filetype = `${attachment?.filetype || ''}`.toLowerCase();
    if (filetype.startsWith('video/')) return true;

    const name = `${attachment?.name || ''}`.toLowerCase();
    return /\.(mp4|mov|avi|mkv|webm)$/.test(name);
  }

  getFileIcon(attachment: any): string {
    const filetype = `${attachment?.filetype || ''}`.toLowerCase();
    const name = `${attachment?.name || ''}`.toLowerCase();

    if (filetype.includes('pdf') || name.endsWith('.pdf')) return 'document-text-outline';
    if (
      filetype.includes('sheet') ||
      filetype.includes('excel') ||
      /\.xlsx?$/.test(name) ||
      name.endsWith('.csv')
    ) {
      return 'grid-outline';
    }
    if (
      filetype.includes('word') ||
      /\.docx?$/.test(name) ||
      name.endsWith('.txt')
    ) {
      return 'document-outline';
    }
    if (
      filetype.includes('zip') ||
      filetype.includes('rar') ||
      /\.(zip|rar|7z|tar|gz)$/.test(name)
    ) {
      return 'archive-outline';
    }
    if (
      filetype.startsWith('audio/') ||
      /\.(mp3|wav|ogg|m4a|aac|flac)$/.test(name)
    ) {
      return 'musical-notes-outline';
    }
    if (
      filetype.startsWith('video/') ||
      /\.(mp4|mov|avi|mkv|webm)$/.test(name)
    ) {
      return 'videocam-outline';
    }

    return 'attach-outline';
  }

  async onAttachmentClick(attachment: any, item: any): Promise<void> {
    if (this.isImageAttachment(attachment) || this.isVideoAttachment(attachment)) {
      const modal = await this.modalCtrl.create({
        component: CommentPreviewModalComponent,
        componentProps: {
          attachment,
          comment: item || null,
        },
      });
      await modal.present();
      return;
    }

    this.downloadAttachment(attachment);
  }

  downloadAttachment(attachment: any): void {
    const url = this.getAttachmentUrl(attachment);
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.download = attachment?.name || 'attachment';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  send() {
    if (!this.canSend) return;

    const title = `${this.comment || ''}`.trim() || '[Attachment]';
    this.commentService
      .save({
        objectType: this.objectType,
        objectId: this.objectId,
        title,
        attachments: this.attachments,
        $populate: ['author']
      })
      .subscribe((resp) => {
        this.comments.push(this.normalizeComment(resp));
        this.comments.sort(
          (a: any, b: any) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
        );
        this.rebuildTimelineGroups();
        this.comment = '';
        this.attachments = [];
      });
  }

  private normalizeComment(item: any): any {
    let parsedAttachments: any[] = [];
    const raw = item?.attachments;
    if (Array.isArray(raw)) {
      parsedAttachments = raw;
    } else if (typeof raw === 'string') {
      try {
        parsedAttachments = JSON.parse(raw);
      } catch {
        parsedAttachments = [];
      }
    }

    const normalized = {
      ...item,
      attachments: parsedAttachments,
    };
    if (!normalized?.author && this.profile?.id && normalized?.userId === this.profile.id) {
      normalized.author = this.profile;
    }
    return normalized;
  }

  private rebuildTimelineGroups(): void {
    const grouped = new Map<string, any[]>();
    this.comments.forEach((item) => {
      const key = this.getDateKey(item?.createdAt);
      const list = grouped.get(key) || [];
      list.push(item);
      grouped.set(key, list);
    });

    this.timelineGroups = Array.from(grouped.entries()).map(([key, items]) => ({
      label: this.getDateLabel(key),
      items,
    }));
  }

  private getDateKey(value: string): string {
    const date = value ? new Date(value) : new Date();
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getDateLabel(key: string): string {
    const today = this.getDateKey(new Date().toISOString());
    if (key === today) return 'Today';

    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = this.getDateKey(yesterdayDate.toISOString());
    if (key === yesterday) return 'Yesterday';

    const [year, month, day] = key.split('-');
    return `${day}/${month}/${year}`;
  }

  private isImageFile(file: File): boolean {
    return `${file?.type || ''}`.toLowerCase().startsWith('image/');
  }

  private async prepareImageForUpload(file: File): Promise<{ file: File; locationText: string }> {
    try {
      const locationInfo = await this.getLocationInfo();
      const timestamp = this.formatTimestamp(new Date());
      const watermarkLines = locationInfo.coordsText ? [timestamp, locationInfo.coordsText] : [timestamp];
      const blob = await this.drawWatermarkToBlob(file, watermarkLines);
      if (!blob) return { file, locationText: locationInfo.displayText };
      return {
        file: new File([blob], file.name, { type: file.type || 'image/jpeg', lastModified: Date.now() }),
        locationText: locationInfo.displayText,
      };
    } catch {
      return { file, locationText: '' };
    }
  }

  private uploadAttachmentFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      this.commonService.uploadFile(file, resolve, reject);
    });
  }

  private async getLocationInfo(): Promise<{ coordsText: string; displayText: string }> {
    const coords = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
      if (!navigator?.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 4000, maximumAge: 0 }
      );
    });

    if (!coords) return { coordsText: '', displayText: '' };

    const lat = coords.lat.toFixed(6);
    const lng = coords.lng.toFixed(6);
    const coordsText = `${lat}, ${lng}`;
    const cityProvince = await this.reverseGeocodeCityProvince(coords.lat, coords.lng);
    return {
      coordsText,
      displayText: cityProvince ? `${cityProvince} (${coordsText})` : coordsText,
    };
  }

  private async reverseGeocodeCityProvince(lat: number, lng: number): Promise<string> {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3500);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=vi`,
        { signal: controller.signal }
      );
      clearTimeout(timer);

      if (!response.ok) return '';
      const data = await response.json();
      const address = data?.address || {};
      const city = address?.city || address?.town || address?.municipality || address?.county || '';
      const province = address?.state || address?.province || '';

      if (city && province && city !== province) return `${city}, ${province}`;
      return city || province || '';
    } catch {
      return '';
    }
  }

  private formatTimestamp(date: Date): string {
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes} ${day}/${month}/${year}`;
  }

  private async drawWatermarkToBlob(file: File, lines: string[]): Promise<Blob | null> {
    const src = await this.readFileAsDataUrl(file);
    const image = await this.loadImage(src);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;

    canvas.width = image.width;
    canvas.height = image.height;
    context.drawImage(image, 0, 0);

    const baseFontSize = Math.max(14, Math.round(canvas.width * 0.02));
    const lineHeight = Math.round(baseFontSize * 1.35);
    const padding = Math.round(baseFontSize * 0.7);
    context.font = `${baseFontSize}px Arial`;

    const contentWidth = Math.max(...lines.map((line) => context.measureText(line).width));
    const boxWidth = Math.round(contentWidth + padding * 2);
    const boxHeight = lineHeight * lines.length + padding * 2;
    const x = canvas.width - boxWidth - padding;
    const y = canvas.height - boxHeight - padding;

    context.fillStyle = 'rgba(0, 0, 0, 0.52)';
    context.fillRect(x, y, boxWidth, boxHeight);
    context.fillStyle = '#ffffff';

    lines.forEach((line, index) => {
      context.fillText(line, x + padding, y + padding + lineHeight * (index + 0.8));
    });

    const outputType = file.type || 'image/jpeg';
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), outputType, 0.92);
    });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(`${reader.result || ''}`);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = src;
    });
  }
}
